import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Plus, Save, ShieldCheck, ExternalLink, Power, Loader2, CheckCircle2, AlertTriangle, X,
} from "lucide-react";
import { axiosInstance } from "../../lib/axios";

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Environment Configuration
//
// Level 1 tabs select the Brand Environment; level 2 tabs select the section of
// its configuration. Everything here is data — the application reads the same
// values at runtime, so a new environment is introduced without any code
// change. Environment-level actions (Preview / Validate / Save / Activate) live
// in the page header rather than as a tab.
// ─────────────────────────────────────────────────────────────────────────────

type Section = "General" | "Portal" | "Branding" | "Email" | "Documents" | "Features" | "Audit";
const SECTIONS: Section[] = ["General", "Portal", "Branding", "Email", "Documents", "Features", "Audit"];

const EMAIL_TYPES = [
  { key: "alerts", label: "Alerts & Notifications", hint: "Account notifications, project & milestone updates, reminders, status changes" },
  { key: "invoice", label: "Invoices", hint: "Invoice creation & delivery, payment notifications and confirmations" },
  { key: "support", label: "Support", hint: "Support requests, client support communication, help/contact notifications" },
];
const DOC_TYPES = [
  { key: "proposal", label: "Proposal" },
  { key: "invoice", label: "Invoice" },
  { key: "report", label: "Report" },
];
const FEATURES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "projects", label: "Projects" },
  { key: "proposals", label: "Proposals" },
  { key: "questionnaires", label: "Questionnaires" },
  { key: "invoices", label: "Invoices" },
  { key: "files", label: "Files" },
  { key: "inbox", label: "Inbox" },
  { key: "reports", label: "Reports" },
];

const input = "w-full px-3 py-2 rounded-lg bg-white border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary outline-none text-sm";
const label = "block text-[12px] font-medium text-on-surface-variant mb-1.5";
const card = "bg-white p-5 rounded-xl border border-outline-variant";

interface EnvSummary {
  _id: string; tenantId: string; name: string; slug: string; subdomain: string; status: string;
  validation?: { ok: boolean; issues: string[] };
}

// Read/write a dotted path on the draft object.
const getPath = (obj: any, path: string) =>
  path.split(".").reduce((o: any, k) => (o === undefined || o === null ? undefined : o[k]), obj);

export default function EnvironmentConfigTab() {
  const [envs, setEnvs] = useState<EnvSummary[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [section, setSection] = useState<Section>("General");
  const [draft, setDraft] = useState<any>(null);
  const [resolved, setResolved] = useState<any>(null);
  const [validation, setValidation] = useState<any>(null);
  const [audit, setAudit] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newEnv, setNewEnv] = useState({ name: "", slug: "", subdomain: "", legalName: "", contactEmail: "" });

  const current = useMemo(() => envs.find((e) => e.tenantId === activeId) || null, [envs, activeId]);

  const loadList = async (select?: string) => {
    try {
      const { data } = await axiosInstance.get("/tenants/environments");
      const list: EnvSummary[] = data.environments || [];
      setEnvs(list);
      const pick = select || activeId || list[0]?.tenantId || "";
      setActiveId(pick);
      return pick;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load environments");
      return "";
    }
  };

  const loadEnv = async (tenantId: string) => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/tenants/environments/${tenantId}`);
      setDraft(data.environment);
      setResolved(data.resolved);
      setValidation(data.validation);
      setDirty(false);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load environment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList().then((id) => id && loadEnv(id)); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (activeId) loadEnv(activeId); /* eslint-disable-next-line */ }, [activeId]);
  useEffect(() => {
    if (section !== "Audit" || !activeId) return;
    axiosInstance.get(`/tenants/environments/${activeId}/audit`)
      .then(({ data }) => setAudit(data.entries || []))
      .catch(() => setAudit([]));
  }, [section, activeId]);

  const set = (path: string, value: any) => {
    setDraft((d: any) => {
      const next = structuredClone(d || {});
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (cur[keys[i]] === undefined || cur[keys[i]] === null) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  const field = (path: string, lbl: string, opts: { placeholder?: string; type?: string; hint?: string } = {}) => (
    <div>
      <label className={label}>{lbl}</label>
      <input
        className={input}
        type={opts.type || "text"}
        value={getPath(draft, path) ?? ""}
        placeholder={opts.placeholder || String(getPath(resolved, path.replace(/^branding\./, "branding.")) ?? "")}
        onChange={(e) => set(path, e.target.value)}
      />
      {opts.hint && <p className="text-[11px] text-outline mt-1">{opts.hint}</p>}
    </div>
  );

  const colorField = (path: string, lbl: string) => {
    const val = String(getPath(draft, path) || "");
    return (
      <div>
        <label className={label}>{lbl}</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={/^#[0-9a-f]{6}$/i.test(val) ? val : "#0058c3"}
            onChange={(e) => set(path, e.target.value)}
            className="w-10 h-9 rounded border border-outline-variant bg-white cursor-pointer"
          />
          <input className={input} value={val} placeholder="#0058c3" onChange={(e) => set(path, e.target.value)} />
        </div>
      </div>
    );
  };

  const save = async () => {
    if (!current) return;
    setBusy("save");
    try {
      const { data } = await axiosInstance.put(`/tenants/environments/${current.tenantId}`, draft);
      setDraft(data.environment);
      setResolved(data.resolved);
      setValidation(data.validation);
      setDirty(false);
      toast.success(data.changes ? `Saved — ${data.changes} setting(s) updated` : "No changes to save");
      loadList(current.tenantId);
    } catch (e: any) {
      const msg = e?.response?.data?.message || "Failed to save configuration";
      if (e?.response?.data?.requiresConfirmation && window.confirm(`${msg}\n\nProceed with the slug change?`)) {
        try {
          const { data } = await axiosInstance.put(`/tenants/environments/${current.tenantId}`, { ...draft, confirmSlugChange: true });
          setDraft(data.environment); setResolved(data.resolved); setValidation(data.validation); setDirty(false);
          toast.success("Saved");
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Failed to save configuration");
        }
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(null);
    }
  };

  const validate = async () => {
    if (!current) return;
    setBusy("validate");
    try {
      const { data } = await axiosInstance.post(`/tenants/environments/${current.tenantId}/validate`);
      setValidation(data.validation);
      toast[data.validation.ok ? "success" : "error"](
        data.validation.ok
          ? "Environment Ready — all required configuration is valid"
          : `${data.validation.issues.length} configuration issue(s) require attention`
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Validation failed");
    } finally {
      setBusy(null);
    }
  };

  const toggleStatus = async () => {
    if (!current) return;
    const next = current.status === "active" ? "inactive" : "active";
    if (next === "inactive" && !window.confirm("Deactivate this environment? Client access is disabled, but no tenant, client, project, invoice, file or configuration data is deleted — it can be reactivated later.")) return;
    setBusy("status");
    try {
      const { data } = await axiosInstance.post(`/tenants/environments/${current.tenantId}/status`, { status: next });
      setValidation(data.validation);
      toast.success(next === "active" ? "Environment activated" : "Environment deactivated — data retained");
      loadList(current.tenantId);
      loadEnv(current.tenantId);
    } catch (e: any) {
      if (e?.response?.data?.validation) setValidation(e.response.data.validation);
      toast.error(e?.response?.data?.message || "Failed to update environment status");
    } finally {
      setBusy(null);
    }
  };

  const createEnv = async () => {
    if (!newEnv.name.trim() || !newEnv.slug.trim() || !newEnv.subdomain.trim()) {
      toast.error("Brand Name, Brand Slug and Subdomain are required");
      return;
    }
    setBusy("create");
    try {
      const { data } = await axiosInstance.post("/tenants/environments", newEnv);
      toast.success("Environment created — complete its configuration, then validate and activate it.");
      setAdding(false);
      setNewEnv({ name: "", slug: "", subdomain: "", legalName: "", contactEmail: "" });
      await loadList(data.environment.tenantId);
      setActiveId(data.environment.tenantId);
      setSection("General");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to create environment");
    } finally {
      setBusy(null);
    }
  };

  if (loading && !draft) {
    return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={26} /></div>;
  }

  return (
    <div className="space-y-5">
      {/* ── Level 1: environment tabs ─────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        {envs.map((e) => (
          <button
            key={e.tenantId}
            onClick={() => setActiveId(e.tenantId)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition flex items-center gap-2 ${
              activeId === e.tenantId
                ? "bg-primary text-white border-primary shadow-sm"
                : "bg-white text-on-surface-variant border-outline-variant hover:bg-surface-container-low"
            }`}
          >
            {e.name}
            <span
              className={`w-1.5 h-1.5 rounded-full ${e.status === "active" ? "bg-emerald-400" : "bg-amber-400"}`}
              title={e.status === "active" ? "Active" : "Inactive"}
            />
          </button>
        ))}
        <button
          onClick={() => setAdding(true)}
          className="px-4 py-2 rounded-lg text-sm font-semibold border border-dashed border-outline-variant text-primary hover:bg-primary/5 inline-flex items-center gap-1.5"
        >
          <Plus size={15} /> Add Environment
        </button>
      </div>

      {current && (
        <>
          {/* ── Header: environment-level actions ───────────────────────── */}
          <div className="bg-white rounded-xl border border-outline-variant p-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[17px] font-bold text-on-surface">{current.name}</h3>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                  current.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                }`}>
                  {current.status === "active" ? "Active" : "Inactive"}
                </span>
                {dirty && <span className="text-[11px] font-semibold text-amber-600">Unsaved changes</span>}
              </div>
              <p className="text-[12px] text-on-surface-variant mt-0.5">
                {current.tenantId} · {current.slug} · {resolved?.portalUrl}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={resolved?.portalUrl || "#"}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-2 rounded-lg text-sm font-semibold border border-outline-variant text-on-surface-variant hover:bg-surface-container-low inline-flex items-center gap-1.5"
                title="Open the client-facing environment using its saved configuration"
              >
                <ExternalLink size={15} /> Preview
              </a>
              <button
                onClick={validate}
                disabled={busy === "validate"}
                className="px-3 py-2 rounded-lg text-sm font-semibold border border-outline-variant text-on-surface-variant hover:bg-surface-container-low inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <ShieldCheck size={15} /> Validate
              </button>
              <button
                onClick={save}
                disabled={busy === "save"}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:brightness-110 inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Save size={15} /> {busy === "save" ? "Saving…" : "Save"}
              </button>
              <button
                onClick={toggleStatus}
                disabled={busy === "status"}
                className={`px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 ${
                  current.status === "active"
                    ? "border border-red-200 text-red-600 hover:bg-red-50"
                    : "bg-emerald-600 text-white hover:brightness-110"
                }`}
              >
                <Power size={15} /> {current.status === "active" ? "Deactivate" : "Activate"}
              </button>
            </div>
          </div>

          {/* ── Validation summary ──────────────────────────────────────── */}
          {validation && (
            <div className={`rounded-xl border p-4 ${validation.ok ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`}>
              <div className="flex items-center gap-2 mb-2">
                {validation.ok
                  ? <CheckCircle2 size={16} className="text-emerald-600" />
                  : <AlertTriangle size={16} className="text-amber-600" />}
                <span className={`text-[13px] font-bold ${validation.ok ? "text-emerald-700" : "text-amber-800"}`}>
                  {validation.ok
                    ? "Environment Ready — required configuration is complete"
                    : `${validation.issues.length} configuration issue(s) require attention`}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                {(validation.groups || []).map((g: any) => (
                  <div key={g.group} className="text-[12px] flex items-start gap-1.5">
                    <span className={g.ok ? "text-emerald-600" : "text-amber-600"}>{g.ok ? "✓" : "•"}</span>
                    <span className="text-on-surface-variant">
                      <span className="font-semibold text-on-surface">{g.group}</span>
                      {g.issues.length > 0 && <span> — {g.issues.join("; ")}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Level 2: configuration tabs ─────────────────────────────── */}
          <div className="flex gap-1 border-b border-outline-variant overflow-x-auto cre-scroll">
            {SECTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setSection(s)}
                className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                  section === s ? "text-primary border-primary" : "text-on-surface-variant border-transparent hover:text-on-surface"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* ── Sections ────────────────────────────────────────────────── */}
          {section === "General" && (
            <div className={card}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field("name", "Brand Name *")}
                {field("branding.legalName", "Legal Company Name")}
                {field("slug", "Brand Slug *", { hint: "Unique identifier used by URLs and stored records — changing it asks for confirmation." })}
                {field("general.website", "Website", { placeholder: "https://…" })}
                {field("general.contactEmail", "Contact Email", { type: "email" })}
                {field("general.contactPhone", "Contact Phone" )}
                <div className="md:col-span-2">
                  <label className={label}>Company Description</label>
                  <textarea
                    className={`${input} h-24 resize-none`}
                    value={getPath(draft, "general.description") ?? ""}
                    onChange={(e) => set("general.description", e.target.value)}
                  />
                </div>
              </div>
              <p className="text-[11px] text-outline mt-3">
                Environment Status is controlled through Activate / Deactivate in the header.
              </p>
            </div>
          )}

          {section === "Portal" && (
            <div className={card}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field("portal.portalName", "Portal Name")}
                {field("subdomain", "Subdomain *", { hint: "Source of truth for every generated client-facing link (login, projects, questionnaires, invoices, proposals, email CTAs, documents)." })}
                {field("branding.supportEmail", "Support Email", { type: "email" })}
                {field("portal.supportPhone", "Support Phone")}
                {field("portal.timezone", "Default Time Zone", { placeholder: "Asia/Karachi" })}
                {field("portal.currency", "Default Currency", { placeholder: "USD" })}
                {field("portal.language", "Default Language", { placeholder: "English" })}
                {field("branding.footerText", "Footer Text")}
              </div>
              {resolved && (
                <p className="text-[12px] text-on-surface-variant mt-3">
                  Resolved portal URL: <span className="font-semibold text-primary">{resolved.portalUrl}</span>
                </p>
              )}
            </div>
          )}

          {section === "Branding" && (
            <div className="space-y-4">
              <div className={card}>
                <h4 className="text-[13px] font-bold text-on-surface mb-3">Assets</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {field("branding.logoUrl", "Primary Logo", { placeholder: "/logo.png or https://…" })}
                  {field("branding.secondaryLogoUrl", "Secondary Logo")}
                  {field("branding.loginLogoUrl", "Login Logo")}
                  {field("branding.faviconUrl", "Favicon")}
                </div>
                {getPath(draft, "branding.logoUrl") && (
                  <img
                    src={String(getPath(draft, "branding.logoUrl"))}
                    alt="Primary logo preview"
                    className="mt-3 h-10 object-contain"
                    onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                  />
                )}
              </div>
              <div className={card}>
                <h4 className="text-[13px] font-bold text-on-surface mb-3">Colors &amp; Typography</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {colorField("branding.primaryColor", "Primary Color")}
                  {colorField("branding.secondaryColor", "Secondary Color")}
                  {colorField("branding.accentColor", "Accent Color")}
                  {colorField("branding.backgroundColor", "Background Color")}
                  {colorField("branding.textColor", "Text Color")}
                  {field("branding.fontFamily", "Font Family", { placeholder: "Inter" })}
                </div>
                <p className="text-[11px] text-outline mt-3">
                  These values are published as design tokens — the client portal, login, emails and documents consume them directly, with no per-brand code.
                </p>
              </div>
            </div>
          )}

          {section === "Email" && (
            <div className="space-y-4">
              {EMAIL_TYPES.map((t) => (
                <div key={t.key} className={card}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h4 className="text-[13px] font-bold text-on-surface">{t.label}</h4>
                      <p className="text-[11px] text-outline">{t.hint}</p>
                    </div>
                    <label className="flex items-center gap-2 text-[12px] font-semibold text-on-surface-variant shrink-0">
                      <input
                        type="checkbox"
                        checked={getPath(draft, `email.${t.key}.active`) !== false}
                        onChange={(e) => set(`email.${t.key}.active`, e.target.checked)}
                      />
                      Active
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {field(`email.${t.key}.senderName`, "Sender Name", { placeholder: resolved?.email?.[t.key]?.senderName })}
                    {field(`email.${t.key}.senderEmail`, "Sender Email", { type: "email", placeholder: resolved?.email?.[t.key]?.senderEmail })}
                    {field(`email.${t.key}.replyTo`, "Reply-To Email", { type: "email", placeholder: resolved?.email?.[t.key]?.replyTo })}
                    {field(`email.${t.key}.logoUrl`, "Email Logo", { placeholder: resolved?.email?.[t.key]?.logoUrl })}
                    {field(`email.${t.key}.footerText`, "Email Footer", { placeholder: resolved?.email?.[t.key]?.footerText })}
                    <div>
                      <label className={label}>Email Signature</label>
                      <textarea
                        className={`${input} h-20 resize-none`}
                        value={getPath(draft, `email.${t.key}.signature`) ?? ""}
                        onChange={(e) => set(`email.${t.key}.signature`, e.target.value)}
                      />
                    </div>
                  </div>
                  {resolved?.email?.[t.key] && (
                    <p className="text-[11px] text-on-surface-variant mt-2">
                      Resolved sender:{" "}
                      <span className="font-semibold text-primary">
                        {resolved.email[t.key].senderName} &lt;{resolved.email[t.key].senderEmail}&gt;
                      </span>
                    </p>
                  )}
                </div>
              ))}
              <p className="text-[11px] text-outline">
                Empty fields fall back to this environment&apos;s own mailbox, then to the platform default for that purpose — the defaults are never hardcoded sender identities.
              </p>
            </div>
          )}

          {section === "Documents" && (
            <div className="space-y-4">
              {DOC_TYPES.map((d) => (
                <div key={d.key} className={card}>
                  <h4 className="text-[13px] font-bold text-on-surface mb-3">{d.label} Branding</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {field(`documents.${d.key}.logoUrl`, "Document Logo", { placeholder: resolved?.documents?.[d.key]?.logoUrl })}
                    {field(`documents.${d.key}.header`, "Document Header", { placeholder: resolved?.documents?.[d.key]?.header })}
                    {field(`documents.${d.key}.footer`, "Document Footer", { placeholder: resolved?.documents?.[d.key]?.footer })}
                    <div>
                      <label className={label}>Company Information</label>
                      <textarea
                        className={`${input} h-20 resize-none`}
                        value={getPath(draft, `documents.${d.key}.companyInfo`) ?? ""}
                        placeholder={resolved?.documents?.[d.key]?.companyInfo}
                        onChange={(e) => set(`documents.${d.key}.companyInfo`, e.target.value)}
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-[12px] font-semibold text-on-surface-variant mt-3">
                    <input
                      type="checkbox"
                      checked={getPath(draft, `documents.${d.key}.showPortalUrl`) !== false}
                      onChange={(e) => set(`documents.${d.key}.showPortalUrl`, e.target.checked)}
                    />
                    Show the portal URL on generated {d.label.toLowerCase()}s
                  </label>
                </div>
              ))}
            </div>
          )}

          {section === "Features" && (
            <div className={card}>
              <h4 className="text-[13px] font-bold text-on-surface mb-1">Client-Facing Modules</h4>
              <p className="text-[11px] text-outline mb-4">
                Disabling a module hides it from client navigation and blocks its API for this environment. Existing data is never deleted — re-enabling restores access immediately.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {FEATURES.map((f) => {
                  const on = getPath(draft, `features.${f.key}`) !== false;
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => set(`features.${f.key}`, !on)}
                      className={`flex items-center justify-between px-3 py-2.5 rounded-lg border text-sm font-semibold transition ${
                        on ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-outline-variant bg-surface-container-low text-outline"
                      }`}
                    >
                      {f.label}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${on ? "bg-emerald-600 text-white" : "bg-slate-300 text-white"}`}>
                        {on ? "ON" : "OFF"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {section === "Audit" && (
            <div className={card}>
              <h4 className="text-[13px] font-bold text-on-surface mb-3">Configuration Changes</h4>
              {audit.length === 0 ? (
                <p className="text-[13px] text-on-surface-variant">No configuration changes recorded yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-[13px] min-w-[720px]">
                    <thead>
                      <tr className="border-b border-outline-variant text-[11px] text-outline uppercase tracking-wider">
                        <th className="py-2 pr-3">Setting Changed</th>
                        <th className="py-2 pr-3">Previous</th>
                        <th className="py-2 pr-3">New</th>
                        <th className="py-2 pr-3">Changed By</th>
                        <th className="py-2 pr-3">Date/Time</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/60">
                      {audit.map((a) => (
                        <tr key={a._id}>
                          <td className="py-2 pr-3 font-semibold text-on-surface">{a.label || a.setting}</td>
                          <td className="py-2 pr-3 text-on-surface-variant break-all">{a.previousValue || "—"}</td>
                          <td className="py-2 pr-3 text-on-surface break-all">{a.newValue || "—"}</td>
                          <td className="py-2 pr-3 text-on-surface-variant">{a.changedBy?.fullName || a.changedByName || "—"}</td>
                          <td className="py-2 pr-3 text-on-surface-variant whitespace-nowrap">
                            {new Date(a.createdAt).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[11px] text-outline mt-3">
                Credentials, secrets and API keys are masked and never stored in the audit trail in plaintext.
              </p>
            </div>
          )}
        </>
      )}

      {/* ── Add Environment ───────────────────────────────────────────────── */}
      {adding && (
        <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setAdding(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant">
              <h3 className="text-lg font-bold text-on-surface">Add Environment</h3>
              <button onClick={() => setAdding(false)} className="p-1.5 rounded-full hover:bg-surface-container text-outline"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={label}>Brand Name *</label>
                <input className={input} value={newEnv.name} placeholder="e.g. NewBrand" onChange={(e) => setNewEnv({ ...newEnv, name: e.target.value })} />
              </div>
              <div>
                <label className={label}>Brand Slug *</label>
                <input className={input} value={newEnv.slug} placeholder="newbrand" onChange={(e) => setNewEnv({ ...newEnv, slug: e.target.value.toLowerCase() })} />
              </div>
              <div>
                <label className={label}>Subdomain *</label>
                <input className={input} value={newEnv.subdomain} placeholder="newbrand.omniworkspace.app" onChange={(e) => setNewEnv({ ...newEnv, subdomain: e.target.value.toLowerCase() })} />
              </div>
              <div>
                <label className={label}>Legal Company Name</label>
                <input className={input} value={newEnv.legalName} onChange={(e) => setNewEnv({ ...newEnv, legalName: e.target.value })} />
              </div>
              <div>
                <label className={label}>Contact Email</label>
                <input className={input} type="email" value={newEnv.contactEmail} onChange={(e) => setNewEnv({ ...newEnv, contactEmail: e.target.value })} />
              </div>
              <p className="text-[11px] text-outline">
                The environment is created as Inactive. Complete its Portal, Branding, Email, Document and Feature configuration, then Validate and Activate it — no code deployment is required.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-outline-variant flex justify-end gap-2">
              <button onClick={() => setAdding(false)} className="px-4 py-2 rounded-lg text-sm font-semibold border border-outline-variant text-on-surface-variant hover:bg-surface-container-low">Cancel</button>
              <button onClick={createEnv} disabled={busy === "create"} className="px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:brightness-110 disabled:opacity-50">
                {busy === "create" ? "Creating…" : "Create Environment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
